import React, { useState, useEffect, useRef } from 'react';
import {
  FiImage,
  FiEdit2,
  FiX,
  FiCheck,
  FiEye,
  FiSend,
  FiLock,
  FiCopy,
  FiExternalLink,
  FiFolder,
  FiGrid,
  FiMail,
  FiHeart,
  FiDownload,
  FiUploadCloud
} from 'react-icons/fi';
import AnnotationReviewList from './AnnotationReviewList';
import { buildVariantUrl } from '../utils/photoCache';
import { UPLOAD_FILE_ACCEPT } from '../utils/supportedExtensions';

const GALLERY_TYPES = [
  { value: 'proofs', label: 'Proofs', description: 'Initial photos for client review' },
  { value: 'selection', label: 'Selection', description: 'Photos for client to choose from' },
  { value: 'finals', label: 'Finals', description: 'Final delivered photos' }
];

const DOWNLOAD_POLICIES = [
  { value: 'none', label: 'No Downloads' },
  { value: 'lowres', label: 'Low Resolution' },
  { value: 'highres', label: 'High Resolution' },
  { value: 'selected', label: 'Selected Only' }
];

// Create/Edit gallery modal
export function GalleryModal({ gallery, jobId, customerId, albums, onSave, onClose, saving }) {
  const [draft, setDraft] = useState({
    title: gallery?.title || '',
    description: gallery?.description || '',
    type: gallery?.type || 'proofs',
    source_type: gallery?.source_type || 'direct',
    album_id: gallery?.album_id || '',
    watermark_enabled: gallery?.watermark_enabled ?? true,
    download_policy: gallery?.download_policy || 'none'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...draft,
      album_id: draft.source_type === 'album' ? draft.album_id : null,
      job_id: jobId,
      customer_id: customerId
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel gallery-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{gallery ? 'Edit Gallery' : 'Create Gallery'}</h3>
          <button onClick={onClose} className="modal-close">
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-section">
              <div className="form-section-header">Gallery Details</div>
              <div className="form-group">
                <label>Gallery Title</label>
                <input
                  type="text"
                  value={draft.title}
                  onChange={e => setDraft(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Wedding Proofs, Final Selects"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={draft.description}
                  onChange={e => setDraft(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description for the gallery"
                  rows={3}
                />
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-header">Settings</div>
              <div className="form-row">
                <div className="form-group">
                  <label>Gallery Type</label>
                  <select
                    value={draft.type}
                    onChange={e => setDraft(prev => ({ ...prev, type: e.target.value }))}
                  >
                    {GALLERY_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Download Policy</label>
                  <select
                    value={draft.download_policy}
                    onChange={e => setDraft(prev => ({ ...prev, download_policy: e.target.value }))}
                  >
                    {DOWNLOAD_POLICIES.map(policy => (
                      <option key={policy.value} value={policy.value}>{policy.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={draft.watermark_enabled}
                    onChange={e => setDraft(prev => ({ ...prev, watermark_enabled: e.target.checked }))}
                  />
                  Enable watermark on images
                </label>
              </div>
            </div>

            {!gallery && (
              <div className="form-section">
                <div className="form-section-header">Photo Source</div>
                <div className="source-type-tabs">
                  <button
                    type="button"
                    className={`source-type-tab ${draft.source_type === 'direct' ? 'active' : ''}`}
                    onClick={() => setDraft(prev => ({ ...prev, source_type: 'direct' }))}
                  >
                    <FiGrid size={24} />
                    <span>Pick Photos</span>
                    <small>Select from library</small>
                  </button>
                  <button
                    type="button"
                    className={`source-type-tab ${draft.source_type === 'album' ? 'active' : ''}`}
                    onClick={() => setDraft(prev => ({ ...prev, source_type: 'album' }))}
                  >
                    <FiFolder size={24} />
                    <span>Link Album</span>
                    <small>Use existing album</small>
                  </button>
                </div>

                {draft.source_type === 'album' && (
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Select Album</label>
                    <select
                      value={draft.album_id}
                      onChange={e => setDraft(prev => ({ ...prev, album_id: e.target.value }))}
                    >
                      <option value="">,  Select an album , </option>
                      {albums.map(album => (
                        <option key={album.id} value={album.id}>
                          {album.title} ({album.image_count || 0} photos)
                        </option>
                      ))}
                    </select>
                    <p className="form-hint">Photos will be pulled from this album</p>
                  </div>
                )}

                {draft.source_type === 'direct' && (
                  <div className="form-hint" style={{ marginTop: '1rem', padding: '0.75rem', background: '#f0f5f4', borderRadius: '8px', border: '1px solid #c2d5d2' }}>
                    <FiImage size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                    After creating the gallery, use "Add Photos" to select photos from your library.
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : gallery ? 'Update Gallery' : 'Create Gallery'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Selection request modal
export function SelectionRequestModal({ gallery, onSave, onClose, saving }) {
  const [draft, setDraft] = useState({
    title: `Selection Request - ${gallery.title}`,
    instructions: '',
    minSelections: '',
    maxSelections: '',
    allowRanking: false,
    allowNotesPerAsset: false,
    dueDate: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      gallery_id: gallery.id,
      title: draft.title,
      instructions: draft.instructions,
      rules: {
        minSelections: draft.minSelections ? parseInt(draft.minSelections) : null,
        maxSelections: draft.maxSelections ? parseInt(draft.maxSelections) : null,
        allowRanking: draft.allowRanking,
        allowNotesPerAsset: draft.allowNotesPerAsset
      },
      due_at: draft.dueDate || null
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel selection-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create Selection Request</h3>
          <button onClick={onClose} className="modal-close">
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="modal-info">
              <FiImage size={16} />
              <span>From gallery: <strong>{gallery.title}</strong> ({gallery.image_count} photos)</span>
            </div>

            <div className="form-group">
              <label>Request Title</label>
              <input
                type="text"
                value={draft.title}
                onChange={e => setDraft(prev => ({ ...prev, title: e.target.value }))}
                required
              />
            </div>

            <div className="form-group">
              <label>Instructions for Client</label>
              <textarea
                value={draft.instructions}
                onChange={e => setDraft(prev => ({ ...prev, instructions: e.target.value }))}
                placeholder="Please select your favorite photos from this gallery..."
                rows={3}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Minimum Selections</label>
                <input
                  type="number"
                  min="0"
                  value={draft.minSelections}
                  onChange={e => setDraft(prev => ({ ...prev, minSelections: e.target.value }))}
                  placeholder="No minimum"
                />
              </div>

              <div className="form-group">
                <label>Maximum Selections</label>
                <input
                  type="number"
                  min="0"
                  value={draft.maxSelections}
                  onChange={e => setDraft(prev => ({ ...prev, maxSelections: e.target.value }))}
                  placeholder="No maximum"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Due Date</label>
              <input
                type="datetime-local"
                value={draft.dueDate}
                onChange={e => setDraft(prev => ({ ...prev, dueDate: e.target.value }))}
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={draft.allowRanking}
                  onChange={e => setDraft(prev => ({ ...prev, allowRanking: e.target.checked }))}
                />
                Allow client to rank their selections
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={draft.allowNotesPerAsset}
                  onChange={e => setDraft(prev => ({ ...prev, allowNotesPerAsset: e.target.checked }))}
                />
                Allow client to add notes per photo
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Creating...' : 'Create Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Publish options modal - choose between proofs (view-only) or selection mode
export function PublishOptionsModal({ gallery, onPublish, onClose, publishing }) {
  const [password, setPassword] = useState('');
  const [shareMode, setShareMode] = useState('proofs');

  const handleSubmit = (e) => {
    e.preventDefault();
    onPublish(password || null, shareMode);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: '460px' }}>
        <div className="modal-header">
          <h3><FiSend /> Publish & Share</h3>
          <button onClick={onClose} className="modal-close">
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="modal-info">
              <FiImage size={16} />
              <span>Gallery: <strong>{gallery.title}</strong></span>
            </div>

            <div className="publish-mode-selector">
              <label className="publish-mode-label">How should your client interact?</label>
              <div className="publish-mode-options">
                <button
                  type="button"
                  className={`publish-mode-option ${shareMode === 'proofs' ? 'active' : ''}`}
                  onClick={() => setShareMode('proofs')}
                >
                  <FiEye size={20} />
                  <strong>Share as Proofs</strong>
                  <span>Client can view, favorite, and annotate, no selection request</span>
                </button>
                <button
                  type="button"
                  className={`publish-mode-option ${shareMode === 'selection' ? 'active' : ''}`}
                  onClick={() => setShareMode('selection')}
                >
                  <FiCheck size={20} />
                  <strong>Share for Selection</strong>
                  <span>Client picks their favorites from a selection request</span>
                </button>
              </div>
            </div>

            <div className="form-group">
              <label><FiLock size={14} style={{ marginRight: '0.5rem' }} />Password Protection (optional)</label>
              <input
                type="text"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Leave blank for no password"
              />
              <p className="form-hint">If set, clients will need to enter this password to view the gallery.</p>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={publishing}>
              {publishing ? 'Publishing...' : shareMode === 'proofs' ? 'Publish as Proofs' : 'Publish for Selection'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Publish success modal
export function PublishSuccessModal({ gallery, token, shareMode, onClose }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/g/${token}`;

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel token-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Gallery Published!</h3>
          <button onClick={onClose} className="modal-close">
            <FiX />
          </button>
        </div>

        <div className="modal-body">
          <div className="token-success">
            <FiCheck size={32} className="success-icon" />
            <p><strong>{gallery.title}</strong> is now published!</p>
            <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
              {shareMode === 'selection'
                ? 'Share link and selection request have been created.'
                : 'Share link has been created. Client can view and annotate proofs.'}
            </p>
          </div>

          <div className="token-url-box">
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Share this link with your client:
            </label>
            <input type="text" value={shareUrl} readOnly />
            <button onClick={copyLink} className="btn-copy">
              {copied ? <FiCheck /> : <FiCopy />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>

          <p className="token-warning">
            <FiLock size={14} />
            Save this link now - it will only be shown once!
          </p>
        </div>

        <div className="modal-footer">
          <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary">
            <FiExternalLink /> Preview as Client
          </a>
          <button onClick={onClose} className="btn-primary">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// Create token modal
export function CreateTokenModal({ gallery, customer, onSave, onClose, saving }) {
  const [draft, setDraft] = useState({
    password: '',
    expiresAt: '',
    allowDownloads: false,
    allowComments: true,
    allowFavorites: true,
    sendEmail: false,
    clientEmail: customer?.email || '',
    clientName: customer?.name || '',
    customMessage: ''
  });
  const [createdToken, setCreatedToken] = useState(null);
  const [copied, setCopied] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await onSave({
      type: 'gallery',
      gallery_id: gallery.id,
      customer_id: gallery.customer_id,
      job_id: gallery.job_id,
      password: draft.password || null,
      expires_at: draft.expiresAt || null,
      allow_downloads: draft.allowDownloads,
      allow_comments: draft.allowComments,
      allow_favorites: draft.allowFavorites,
      send_email: draft.sendEmail,
      client_email: draft.sendEmail ? draft.clientEmail : null,
      client_name: draft.sendEmail ? draft.clientName : null,
      custom_message: draft.sendEmail ? draft.customMessage : null
    });
    if (result?.token) {
      setCreatedToken(result.token);
      setEmailSent(result.email_sent);
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/g/${createdToken}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (createdToken) {
    const shareUrl = `${window.location.origin}/g/${createdToken}`;
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-panel token-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Share Link Created</h3>
            <button onClick={onClose} className="modal-close">
              <FiX />
            </button>
          </div>

          <div className="modal-body">
            <div className="token-success">
              <FiCheck size={32} className="success-icon" />
              <p>Your share link has been created!</p>
              {emailSent && (
                <p style={{ color: '#22c55e', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  <FiMail size={14} style={{ marginRight: '0.25rem', verticalAlign: 'middle' }} />
                  Email sent to {draft.clientEmail}
                </p>
              )}
            </div>

            <div className="token-url-box">
              <input type="text" value={shareUrl} readOnly />
              <button onClick={copyLink} className="btn-copy">
                {copied ? <FiCheck /> : <FiCopy />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>

            <p className="token-warning">
              <FiLock size={14} />
              This link will only be shown once. Save it now!
            </p>
          </div>

          <div className="modal-footer">
            <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary">
              <FiExternalLink /> Preview
            </a>
            <button onClick={onClose} className="btn-primary">
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel token-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create Share Link</h3>
          <button onClick={onClose} className="modal-close">
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="modal-info">
              <FiImage size={16} />
              <span>Gallery: <strong>{gallery.title}</strong></span>
            </div>

            <div className="form-group">
              <label>Password Protection (optional)</label>
              <input
                type="text"
                value={draft.password}
                onChange={e => setDraft(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Leave blank for no password"
              />
            </div>

            <div className="form-group">
              <label>Expiration Date (optional)</label>
              <input
                type="datetime-local"
                value={draft.expiresAt}
                onChange={e => setDraft(prev => ({ ...prev, expiresAt: e.target.value }))}
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={draft.allowDownloads}
                  onChange={e => setDraft(prev => ({ ...prev, allowDownloads: e.target.checked }))}
                />
                Allow downloads
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={draft.allowFavorites}
                  onChange={e => setDraft(prev => ({ ...prev, allowFavorites: e.target.checked }))}
                />
                Allow marking favorites
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={draft.allowComments}
                  onChange={e => setDraft(prev => ({ ...prev, allowComments: e.target.checked }))}
                />
                Allow comments
              </label>
            </div>

            <div className="email-section">
              <div className="email-section-header">
                <label>
                  <input
                    type="checkbox"
                    checked={draft.sendEmail}
                    onChange={e => setDraft(prev => ({ ...prev, sendEmail: e.target.checked }))}
                  />
                  <FiMail size={16} />
                  Send email notification to client
                </label>
              </div>

              {draft.sendEmail && (
                <div className="email-section-fields">
                  <div className="form-group">
                    <label>Client Email</label>
                    <input
                      type="email"
                      value={draft.clientEmail}
                      onChange={e => setDraft(prev => ({ ...prev, clientEmail: e.target.value }))}
                      placeholder="client@example.com"
                      required={draft.sendEmail}
                    />
                  </div>

                  <div className="form-group">
                    <label>Client Name</label>
                    <input
                      type="text"
                      value={draft.clientName}
                      onChange={e => setDraft(prev => ({ ...prev, clientName: e.target.value }))}
                      placeholder="Client's name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Personal Message (optional)</label>
                    <textarea
                      value={draft.customMessage}
                      onChange={e => setDraft(prev => ({ ...prev, customMessage: e.target.value }))}
                      placeholder="Add a personal message to your client..."
                      rows={2}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Creating...' : draft.sendEmail ? 'Create & Send Email' : 'Create Link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Feedback review modal (likes + annotations)
export function FeedbackReviewModal({ gallery, images, loading, error, onClose, onReplace, replacingId }) {
  const inputRef = useRef(null);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [activeTab, setActiveTab] = useState('likes');

  useEffect(() => {
    setActiveTab('likes');
  }, [gallery?.id]);

  const openReplace = (image) => {
    setReplaceTarget(image);
    inputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && replaceTarget) {
      onReplace(replaceTarget, file);
    }
    if (e.target) e.target.value = '';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel favorites-modal feedback-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Review Feedback{gallery?.title ? ` · ${gallery.title}` : ''}</h3>
          <button onClick={onClose} className="modal-close">
            <FiX />
          </button>
        </div>

        <div className="modal-body">
          <div className="feedback-tabs">
            <button
              type="button"
              className={`feedback-tab ${activeTab === 'likes' ? 'active' : ''}`}
              onClick={() => setActiveTab('likes')}
            >
              <FiHeart /> Likes
            </button>
            <button
              type="button"
              className={`feedback-tab ${activeTab === 'annotations' ? 'active' : ''}`}
              onClick={() => setActiveTab('annotations')}
            >
              <FiEdit2 /> Annotations
            </button>
          </div>

          {activeTab === 'likes' && (
            <>
              {loading && <div className="favorites-loading">Loading liked photos...</div>}
              {error && <div className="favorites-error">{error}</div>}
              {!loading && !error && images.length === 0 && (
                <div className="favorites-empty">
                  <FiHeart size={36} />
                  <p>No liked photos yet</p>
                </div>
              )}

              {!loading && !error && images.length > 0 && (
                <div className="favorites-list">
                  {images.map((img) => {
                    const token = window?.localStorage?.getItem('auth_token');
                    const downloadUrl = `/media/originals/${img.filename}`;
                    const thumb = buildVariantUrl(img, 'md');
                    return (
                      <div key={img.id} className="favorites-row">
                        <div className="favorites-thumb">
                          {thumb ? <img src={thumb} alt={img.title || img.filename || 'Photo'} /> : <div className="thumb-fallback" />}
                        </div>
                        <div className="favorites-meta">
                          <div className="favorites-title">
                            <strong>{img.title || img.filename || 'Untitled'}</strong>
                            {img.title && img.filename && <span className="favorites-filename">{img.filename}</span>}
                          </div>
                          <div className="favorites-sub">
                            <span>{img.like_count || 0} like{img.like_count === 1 ? '' : 's'}</span>
                            {img.liked_by_list && img.liked_by_list.length > 0 && (
                              <span className="favorites-likedby"> · {img.liked_by_list.slice(0, 3).join(', ')}{img.liked_by_list.length > 3 ? '…' : ''}</span>
                            )}
                          </div>
                        </div>
                        <div className="favorites-actions">
                          <a href={downloadUrl} className="btn-secondary" target="_blank" rel="noopener noreferrer">
                            <FiDownload /> Original
                          </a>
                          <button className="btn-primary" onClick={() => openReplace(img)} disabled={replacingId === img.id}>
                            <FiUploadCloud /> {replacingId === img.id ? 'Replacing...' : 'Replace'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {activeTab === 'annotations' && (
            <div className="feedback-annotations">
              <AnnotationReviewList galleryId={gallery?.id} headers={{}} />
            </div>
          )}
        </div>

        <input
          ref={inputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
          accept={UPLOAD_FILE_ACCEPT}
        />
      </div>
    </div>
  );
}
